/**
 * The shared review screen's model (#1961, slice 2D of #1948).
 *
 * Every proposed change to a live roster — a *Fix and re-solve* repair, a
 * corrections draft (absorb / combine / stretch), later the cross-tier
 * knock-on — is reviewed on ONE screen: only the people who move, one row
 * each, their before slots against their after slots, the ladder's
 * candidates for the same cells under the row, accept or reject per row,
 * one Apply. This module is the pure half: grouping cell changes into
 * per-person rows, the swap ("Use <name> instead"), and the decision the
 * Apply hands back to the caller.
 *
 * Units: a CELL is one member on one day (the unit the repair and the
 * corrections apply count as `changed_cells`); a SLOT is one session of a
 * cell (date · session · station).
 */
import { ApiError } from "../api/client";

/** One person's alternative for a cell — the ladder's rung-0 candidate. */
export interface ReviewAlternative {
  membership_id: string;
  display_name: string;
  /** What they do that day (the ladder's `doing`), shown after the name. */
  reason?: string | undefined;
}

/** One changed slot. `null` = empty (vacated / not seated). On-call rows read
 *  session `ONCALL` with the on-call key as the station. */
export interface ReviewChange {
  membership_id: string;
  display_name: string;
  date: string;
  session: string;
  before_station: string | null;
  after_station: string | null;
  /** Who else could take `after_station` on that date and session. */
  alternatives?: ReviewAlternative[] | undefined;
}

/** A fixed allocation the change was solved around. */
export interface ReviewPin {
  membership_id: string;
  date: string;
  off?: boolean | undefined;
  station?: string | null | undefined;
  session?: string | null | undefined;
}

export type SlotKind = "vacated" | "filled";

export interface ReviewSlot {
  date: string;
  session: string;
  station: string;
  kind: SlotKind;
}

/** A filled slot someone else could take instead. */
export interface ReviewSwapOption {
  /** `membership|date|session` of the change it replaces. */
  changeKey: string;
  slot: ReviewSlot;
  alternatives: ReviewAlternative[];
}

export interface ReviewRow {
  /** Stable row id: the member, or `member|swap|changeKey` for a swap row. */
  key: string;
  membership_id: string;
  display_name: string;
  before: ReviewSlot[];
  after: ReviewSlot[];
  /** Distinct dates this row changes — its cells. */
  dates: string[];
  /** A row that holds a pin cannot be rejected. */
  pinned: boolean;
  /** #1988 — the pins that make the row pinned (the review's Unpin). */
  pins?: ReviewPin[] | undefined;
  options: ReviewSwapOption[];
  /** Set on a swap row: the person the draft had in this seat. */
  insteadOf?: { membership_id: string; display_name: string; changeKey: string } | undefined;
}

/** A seat handed to someone else (`Use <name> instead`). */
export interface ReviewSeat {
  membership_id: string;
  date: string;
  session: string;
  station: string;
}

export interface ReviewCell {
  membership_id: string;
  date: string;
}

/** What Apply hands the caller. */
export interface ReviewDecision {
  /** Cells left exactly as the live roster has them: every cell of a
   *  rejected row, and the draft holder's cell under a swap. */
  skip: ReviewCell[];
  /** Seats handed to someone else instead of the draft's holder. */
  seat: ReviewSeat[];
  /** Cells that will change. */
  count: number;
}

export function changeKey(c: Pick<ReviewChange, "membership_id" | "date" | "session">): string {
  return `${c.membership_id}|${c.date}|${c.session}`;
}

function isMove(c: ReviewChange): boolean {
  return (c.before_station ?? null) !== (c.after_station ?? null);
}

function pinTouches(pin: ReviewPin, c: ReviewChange): boolean {
  if (pin.membership_id !== c.membership_id || pin.date !== c.date) return false;
  if (pin.off) return true;
  if (!pin.station) return false;
  if (c.after_station !== pin.station && c.before_station !== pin.station) return false;
  return !pin.session || pin.session === c.session || pin.session === "FULL_DAY";
}

const SESSION_ORDER = ["AM", "PM", "FULL_DAY", "ONCALL"];
function slotOrder(a: ReviewSlot, b: ReviewSlot): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const sa = SESSION_ORDER.indexOf(a.session);
  const sb = SESSION_ORDER.indexOf(b.session);
  return (sa === -1 ? 99 : sa) - (sb === -1 ? 99 : sb);
}

/**
 * Group cell changes into one row per person who moves. A change whose
 * before and after are the same is not a move and is dropped; a person with
 * no move left has no row. Rows run by their first date, then by name.
 *
 * `swaps` maps a change key to the alternative chosen for its filled slot:
 * the draft holder loses that DATE's changes (their cell stays as it is) and
 * a swap row seats the alternative on the slot instead.
 */
export function groupChangesByPerson(
  changes: readonly ReviewChange[],
  pins: readonly ReviewPin[] = [],
  swaps: Readonly<Record<string, ReviewAlternative>> = {},
): ReviewRow[] {
  const moves = changes.filter(isMove);
  // A swapped change takes its holder's whole cell (that date) out of the draft.
  const swappedCells = new Set<string>();
  const swapRows: ReviewRow[] = [];
  for (const c of moves) {
    const key = changeKey(c);
    const alt = swaps[key];
    if (!alt || !c.after_station) continue;
    swappedCells.add(`${c.membership_id}|${c.date}`);
    swapRows.push({
      key: `${alt.membership_id}|swap|${key}`,
      membership_id: alt.membership_id,
      display_name: alt.display_name,
      before: [],
      after: [{ date: c.date, session: c.session, station: c.after_station, kind: "filled" }],
      dates: [c.date],
      pinned: false,
      options: [],
      insteadOf: { membership_id: c.membership_id, display_name: c.display_name, changeKey: key },
    });
  }

  // Who moves on which date: an alternative already moving that day is not
  // offered (they would be in two places).
  const movingOn = new Set(moves.map((c) => `${c.membership_id}|${c.date}`));
  const offPinned = new Set(pins.filter((p) => p.off).map((p) => `${p.membership_id}|${p.date}`));

  const byPerson = new Map<string, ReviewRow>();
  for (const c of moves) {
    if (swappedCells.has(`${c.membership_id}|${c.date}`)) continue;
    let row = byPerson.get(c.membership_id);
    if (!row) {
      row = {
        key: c.membership_id,
        membership_id: c.membership_id,
        display_name: c.display_name,
        before: [],
        after: [],
        dates: [],
        pinned: false,
        options: [],
      };
      byPerson.set(c.membership_id, row);
    }
    if (!row.dates.includes(c.date)) row.dates.push(c.date);
    if (c.before_station) {
      row.before.push({
        date: c.date,
        session: c.session,
        station: c.before_station,
        kind: "vacated",
      });
    }
    if (c.after_station) {
      const slot: ReviewSlot = {
        date: c.date,
        session: c.session,
        station: c.after_station,
        kind: "filled",
      };
      row.after.push(slot);
      const alternatives = (c.alternatives ?? []).filter(
        (a) =>
          a.membership_id !== c.membership_id &&
          !movingOn.has(`${a.membership_id}|${c.date}`) &&
          !offPinned.has(`${a.membership_id}|${c.date}`),
      );
      if (alternatives.length > 0) {
        row.options.push({ changeKey: changeKey(c), slot, alternatives });
      }
    }
    for (const p of pins) {
      if (!pinTouches(p, c)) continue;
      row.pinned = true;
      row.pins ??= [];
      if (!row.pins.includes(p)) row.pins.push(p);
    }
  }
  const rows = [...byPerson.values(), ...swapRows];
  for (const row of rows) {
    row.before.sort(slotOrder);
    row.after.sort(slotOrder);
    row.dates.sort();
    // A pinned row's seat is the lead's own choice: no alternatives.
    if (row.pinned) row.options = [];
  }
  rows.sort((a, b) => {
    const da = a.dates[0] ?? "";
    const db = b.dates[0] ?? "";
    if (da !== db) return da < db ? -1 : 1;
    return a.display_name.localeCompare(b.display_name);
  });
  return rows;
}

/**
 * The decision Apply hands the caller: `rejected` holds the keys of rows the
 * lead rejected (a pinned row is never rejected).
 */
export function reviewDecision(
  rows: readonly ReviewRow[],
  rejected: ReadonlySet<string>,
): ReviewDecision {
  const skip = new Map<string, ReviewCell>();
  const seat: ReviewSeat[] = [];
  const changing = new Set<string>();
  for (const row of rows) {
    const isRejected = rejected.has(row.key) && !row.pinned;
    if (row.insteadOf) {
      const [holder, date] = row.insteadOf.changeKey.split("|");
      if (holder && date) skip.set(`${holder}|${date}`, { membership_id: holder, date });
      if (isRejected) continue;
      for (const s of row.after) {
        seat.push({
          membership_id: row.membership_id,
          date: s.date,
          session: s.session,
          station: s.station,
        });
        changing.add(`${row.membership_id}|${s.date}`);
      }
      continue;
    }
    for (const date of row.dates) {
      const key = `${row.membership_id}|${date}`;
      if (isRejected) skip.set(key, { membership_id: row.membership_id, date });
      else changing.add(key);
    }
  }
  return { skip: [...skip.values()], seat, count: changing.size };
}

// ─── Roles ↔ slots (for callers that hold assignment rows) ────────────────

const STATION_SESSIONS = ["FULL_DAY", "NIGHT", "AM", "PM"];

/** `STATION_55_AM` → `{session: "AM", station: "55"}`; `ONCALL_night` →
 *  `{session: "ONCALL", station: "night"}`. */
export function splitRole(role: string): { session: string; station: string } {
  const upper = role.toUpperCase();
  if (upper.startsWith("STATION_")) {
    const body = role.slice("STATION_".length);
    for (const suffix of STATION_SESSIONS) {
      if (body.toUpperCase().endsWith(`_${suffix}`) && body.length > suffix.length + 1) {
        return { session: suffix, station: body.slice(0, -(suffix.length + 1)) };
      }
    }
    return { session: "FULL_DAY", station: body };
  }
  if (upper.startsWith("ONCALL_")) return { session: "ONCALL", station: role.slice(7) };
  return { session: "ONCALL", station: role };
}

/** The inverse of `splitRole`. */
export function roleFor(session: string, station: string): string {
  return session === "ONCALL" ? `ONCALL_${station}` : `STATION_${station}_${session}`;
}

export interface RosterRow {
  membership_id: string;
  date: string;
  role: string;
}

/**
 * The cell changes between two sets of roster rows (live → proposed), one
 * per (member, date, session) whose station differs — the shape the repair
 * endpoint returns, built client-side for drafts that send a whole roster
 * (the corrections stages).
 */
export function diffRosterRows(
  live: readonly RosterRow[],
  proposed: readonly RosterRow[],
  nameOf: (membershipId: string) => string,
): ReviewChange[] {
  const index = (rows: readonly RosterRow[]) => {
    const out = new Map<string, Map<string, string[]>>();
    for (const r of rows) {
      const cell = `${r.membership_id}|${r.date}`;
      const { session, station } = splitRole(r.role);
      let bySession = out.get(cell);
      if (!bySession) {
        bySession = new Map();
        out.set(cell, bySession);
      }
      bySession.set(session, [...(bySession.get(session) ?? []), station].sort());
    }
    return out;
  };
  const before = index(live);
  const after = index(proposed);
  const out: ReviewChange[] = [];
  for (const cell of new Set([...before.keys(), ...after.keys()])) {
    const b = before.get(cell) ?? new Map<string, string[]>();
    const a = after.get(cell) ?? new Map<string, string[]>();
    const [membership_id = "", date = ""] = cell.split("|");
    for (const session of new Set([...b.keys(), ...a.keys()])) {
      const bs = (b.get(session) ?? []).join(", ") || null;
      const as = (a.get(session) ?? []).join(", ") || null;
      if (bs === as) continue;
      out.push({
        membership_id,
        display_name: nameOf(membership_id),
        date,
        session,
        before_station: bs,
        after_station: as,
      });
    }
  }
  out.sort((x, y) => (x.date === y.date ? 0 : x.date < y.date ? -1 : 1));
  return out;
}

/**
 * Apply a decision to a whole-roster draft: a skipped cell keeps its live
 * rows, a seat is added to its new holder's rows.
 */
export function decideRosterRows<T extends RosterRow>(
  live: readonly T[],
  proposed: readonly T[],
  decision: ReviewDecision,
  make: (row: RosterRow) => T,
): T[] {
  const skipped = new Set(decision.skip.map((c) => `${c.membership_id}|${c.date}`));
  const out = proposed.filter((r) => !skipped.has(`${r.membership_id}|${r.date}`));
  out.push(...live.filter((r) => skipped.has(`${r.membership_id}|${r.date}`)));
  for (const s of decision.seat) {
    out.push(
      make({ membership_id: s.membership_id, date: s.date, role: roleFor(s.session, s.station) }),
    );
  }
  return out;
}

// ─── A refused Apply ───────────────────────────────────────────────────────

export interface ConflictCell {
  membership_id: string;
  date: string;
  /** The seat named by the refusal, when it names one. */
  station?: string | undefined;
  session?: string | undefined;
}

/** The cells a 409 names (`{error: {detail: {cells: […]}}}`), or null when
 *  the error is not a 409. An empty list is a 409 that names no cell. */
export function conflictCells(err: unknown): ConflictCell[] | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: { detail?: unknown } } | null;
  const detail = body?.error?.detail as { cells?: unknown } | undefined;
  const raw = Array.isArray(detail?.cells) ? detail.cells : [];
  const out: ConflictCell[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const c = item as {
      membership_id?: unknown;
      date?: unknown;
      role?: unknown;
      pin?: { station?: unknown; session?: unknown } | null;
    };
    if (typeof c.membership_id !== "string" || typeof c.date !== "string") continue;
    const cell: ConflictCell = { membership_id: c.membership_id, date: c.date };
    if (typeof c.role === "string") {
      const { session, station } = splitRole(c.role);
      cell.station = station;
      cell.session = session;
    } else if (c.pin && typeof c.pin.station === "string") {
      cell.station = c.pin.station;
      if (typeof c.pin.session === "string") cell.session = c.pin.session;
    }
    out.push(cell);
  }
  return out;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `Fri 2 Oct` — a slot's date, in the roster's own words. */
export function reviewDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[dt.getUTCDay()]} ${d} ${MONTHS[m - 1]}`;
}

/** `AM` / `PM` / `Full day` / `On call`. */
export function reviewSession(session: string): string {
  if (session === "FULL_DAY") return "Full day";
  if (session === "ONCALL") return "On call";
  if (session === "NIGHT") return "Night";
  return session;
}
